<template>
  <v-row dense align="center" class="mb-0 filtre-comenzi-row">
    <!-- Select Categorie -->
    <v-col cols="12" sm="4">
      <DxSelectBox
        v-model="selectedCategory"
        :items="categories"
        display-expr="Denumire"
        value-expr="TipComanda"
        placeholder="Categorie"
        :search-enabled="true"
        search-expr="Denumire"
      />
    </v-col>

    <!-- Data Start -->
    <v-col cols="12" sm="2">
      <DxDateBox
        v-model="dataStart"
        type="date"
        display-format="yyyy-MM-dd"
        show-clear-button="true"
        placeholder="Start"
      />
    </v-col>

    <!-- Data End -->
    <v-col cols="12" sm="2">
      <DxDateBox
        v-model="dataEnd"
        type="date"
        display-format="yyyy-MM-dd"
        show-clear-button="true"
        placeholder="End"
        :min="dataStart"
      />
    </v-col>

    <!-- Select Produs -->
    <v-col cols="12" sm="4">
      <DxSelectBox
        v-model="selectedProdusId"
        :data-source="dsProduse"
        :display-expr="produsDisplayExpr"
        value-expr="Id"
        placeholder="Produs"
        :search-enabled="true"
        :search-expr="['Cod', 'Nume']"
        :show-clear-button="true"
      />
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import DxDateBox from "devextreme-vue/date-box";
import DxSelectBox from "devextreme-vue/select-box";
import DataSource from "devextreme/data/data_source";
import ODataStore from "devextreme/data/odata/store";
import CustomStore from "devextreme/data/custom_store";
import ProComenziService from "@/services/Comercial/ProComenzi";
import AppSetting from "@/settings/appSettings";

interface Filter {
  category: number | null;
  start: string;
  end: string;
  produsId: string | null;
}

const emit = defineEmits<{
  (e: "filtersChanged", filters: Filter): void;
}>();

const STORAGE_KEY = 'filtre-comenzi';

function loadSaved(): { category: number | null; start: Date | null; end: Date | null; produsId: string | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { category: null, start: null, end: null, produsId: null };
    const parsed = JSON.parse(raw);
    return {
      category: parsed.category ?? null,
      start: parsed.start ? new Date(parsed.start) : null,
      end: parsed.end ? new Date(parsed.end) : null,
      produsId: parsed.produsId ?? null,
    };
  } catch {
    return { category: null, start: null, end: null, produsId: null };
  }
}

function savedOrDefault() {
  const saved = loadSaved();
  const now = new Date();
  return {
    category: saved.category,
    start: saved.start ?? new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(),
    produsId: saved.produsId,
  };
}

const initial = savedOrDefault();
const selectedCategory = ref<number | null>(initial.category);
const categories = ref<any[]>([]);
const dataStart = ref<Date | null>(initial.start);
const dataEnd = ref<Date | null>(initial.end);
const selectedProdusId = ref<string | null>(initial.produsId);

const token = localStorage.getItem('ERA_Jwt') ?? '';

function campCondition(camp: string, cuvant: string) {
  return [camp, 'contains', cuvant];
}

// O condiție per cuvânt căutat: cuvântul trebuie să apară în ORICE câmp din searchExpr (Cod/Nume),
// combinate cu 'or'. Condițiile per cuvânt se combină între ele cu 'and' — fiecare cuvânt introdus
// trebuie să apară undeva, indiferent de ordine, ca la căutarea de produs din liniile comenzii
// (vezi produsNumeFilterExpression din GridComandaDetalii.vue — "cafea aparat" trebuie să găsească
// "Aparat cafea").
function cuvantCondition(campuri: string[], cuvant: string): any {
  if (campuri.length === 1) return campCondition(campuri[0], cuvant);
  const parti: any[] = [];
  campuri.forEach((camp, i) => {
    if (i > 0) parti.push('or');
    parti.push(campCondition(camp, cuvant));
  });
  return parti;
}

const produseBaseStore = new ODataStore({
  url: AppSetting.API_Url_ComercialOdata + 'PRO_Produse',
  key: 'Id', keyType: 'Guid', version: 4,
  beforeSend: (e: any) => { e.headers = { Authorization: 'Bearer ' + token }; },
});

const dsProduse = new DataSource({
  store: new CustomStore({
    key: 'Id',
    load: (loadOptions: any) => {
      const opts = { ...loadOptions };
      if (typeof opts.searchValue === 'string' && opts.searchValue.trim()) {
        const campuri: string[] = Array.isArray(opts.searchExpr) ? opts.searchExpr : [opts.searchExpr];
        const cuvinte = opts.searchValue.trim().split(/\s+/);
        let filtruFinal: any = cuvantCondition(campuri, cuvinte[0]);
        for (let i = 1; i < cuvinte.length; i++)
          filtruFinal = [filtruFinal, 'and', cuvantCondition(campuri, cuvinte[i])];
        opts.filter = opts.filter ? [opts.filter, 'and', filtruFinal] : filtruFinal;
        opts.searchValue = null;
        opts.searchExpr = null;
        opts.searchOperation = null;
      }
      return produseBaseStore.load(opts);
    },
    byKey: (key: any) => produseBaseStore.byKey(key),
  }),
  select: ['Id', 'Cod', 'Nume'],
  sort: [{ selector: 'Cod', desc: false }],
  paginate: true, pageSize: 50,
});
function produsDisplayExpr(item: any) {
  return item ? `${item.Cod ?? ''} - ${item.Nume ?? ''}` : '';
}

// Funcție de formatare ISO yyyy-MM-dd
function formatDate(d: Date | null) {
  return d ? d.toISOString().split("T")[0] : "";
}

// Emitere filtre
function applyFilters() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    category: selectedCategory.value,
    start: dataStart.value?.toISOString() ?? null,
    end: dataEnd.value?.toISOString() ?? null,
    produsId: selectedProdusId.value,
  }));
  emit("filtersChanged", {
    category: selectedCategory.value !== null ? Number(selectedCategory.value) : null,
    start: formatDate(dataStart.value),
    end: formatDate(dataEnd.value),
    produsId: selectedProdusId.value,
  });
}

// Aplică automat filtrele la orice schimbare (fără buton "Caută").
watch([selectedCategory, dataStart, dataEnd, selectedProdusId], applyFilters);

// Încărcare categorii + aplicare filtre implicite
onMounted(async () => {
  const resp = await ProComenziService.getCategoriiPermise();
  categories.value = resp.data ?? [];
  if (selectedCategory.value === null && categories.value.length > 0)
    selectedCategory.value = categories.value[0].TipComanda;
  applyFilters();
});
</script>

<style scoped>
.filtre-comenzi-row :deep(.dx-texteditor-input) {
  height: 26px !important;
  min-height: unset !important;
  padding-top: 3px !important;
  padding-bottom: 3px !important;
  font-size: 12px !important;
}

.filtre-comenzi-row :deep(.dx-texteditor) {
  height: 26px !important;
}

.filtre-comenzi-row :deep(.dx-placeholder) {
  font-size: 12px !important;
  padding-top: 3px !important;
}

/* Textul placeholder-ului e randat prin ::before, cu propriul padding-top: 9px din tema
   DevExtreme (calibrat pentru inputul standard, mai înalt) — se adună cu padding-top-ul de mai
   sus și împinge textul sub centrul cutiei comprimate la 26px. Compensăm separat aici. */
.filtre-comenzi-row :deep(.dx-placeholder)::before {
  padding-top: 2px !important;
}

.filtre-comenzi-row :deep(.dx-dropdowneditor-icon),
.filtre-comenzi-row :deep(.dx-clear-button-area) {
  height: 26px !important;
  width: 26px !important;
}
</style>
